import Container from "../../../components/element/container";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MDBModalBody } from "mdb-react-ui-kit";
import { useDispatch, useSelector } from "react-redux";
import Icon from "../../../components/element/icon";
import FullModal from "../../../components/modal/full.modal";
import { showToast } from "../../../helpers/show.toast";
import { closeReservationModal } from "../../../redux/reducer/modal.reducer";
import { createOrder } from "../../../redux/reducer/order.reducer";
import { useNavigate } from "react-router-dom";
import PolicyReaderModal from "../../../components/modal/policy.reader.modal";
import { reservationPolicy } from "../../../content/reservation.policy.mjs";
import {
  getAppliedReferral,
  validateAppliedReferral,
} from "../../../helpers/applied.referral";

export default function ReserveVehicleModal({ productId, productName }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [validatingReferral, setValidatingReferral] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const { isOpenReservationModal } = useSelector((state) => state.modal);
  const { createOrderLoading } = useSelector((state) => state.order);

  const close = () => {
    setAgreed(false);
    setShowPolicy(false);
    dispatch(closeReservationModal());
  };

  const validityDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 14);
    return date.toLocaleDateString("en-MY", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }, []);

  const details = [
    ["famicons:car-sport", "Vehicle", productName || "Car Model Name"],
    ["vaadin:money", "Reservation Fees", "RM5,000 (Refundable)"],
    [
      "boxicons:calendar-alt",
      "Reservation Validity",
      `14 Days (Until ${validityDate})`,
    ],
    [
      "boxicons:clock-dashed-half-filled",
      "Next Step",
      "Our Team Follow You in 24hrs",
    ],
  ];

  const handleReserve = async () => {
    if (!productId || !agreed || createOrderLoading || validatingReferral)
      return;
    setValidatingReferral(true);
    let referralCode;
    try {
      referralCode = await validateAppliedReferral(getAppliedReferral());
    } catch (error) {
      showToast(
        "Unable to reserve vehicle",
        error.message || "Unable to validate referral code. Please try again.",
        "error",
      );
      setValidatingReferral(false);
      return;
    }

    dispatch(
      createOrder({
        product_id: productId,
        ...(referralCode ? { referral_code: referralCode } : {}),
      }),
    )
      .unwrap()
      .then((response) => {
        const reservation = response?.data || {};
        const orderId =
          reservation.order_id ??
          reservation.order?.id ??
          reservation.order?.order_id ??
          null;

        showToast(
          "Reservation created",
          "Choose a payment method from your purchases to continue.",
          "success",
        );
        close();
        navigate("/purchases", {
          state: {
            paymentAfterReserve: {
              orderId,
              productId,
            },
          },
        });
      })
      .catch((error) => {
        showToast(
          "Unable to reserve vehicle",
          error?.response?.data?.message ||
            "Please check the vehicle availability and try again.",
          "error",
        );
      })
      .finally(() => setValidatingReferral(false));
  };

  return (
    <>
      <FullModal
        independent
        show={isOpenReservationModal}
        close={close}
        showCloseButton
        centered
        screenSize="lg"
        className="product-action-modal reservation-modal overflow-hidden"
        dialogClassName="action-modal-dialog reservation-dialog"
        contentClassName="action-modal-content reservation-content"
        content={
          <MDBModalBody className="action-modal-body reservation-body">
            <h2 id="reservation-title">{t("Reserve This Vehicle")}</h2>
            <p className="reservation-lead security-note-inline">
              <Icon
                tag="span"
                icon="solar:lock-keyhole-linear"
                className="security-lock"
              />
              {t("You’re one step closer to making this car yours!")}
            </p>
            <Container tag="div" className="reservation-details">
              {details.map(([icon, label, value]) => (
                <article className="reservation-detail" key={label}>
                  <Icon icon={icon} className="reservation-detail-icon" />
                  <span>{label}</span>
                  <p>{value}</p>
                </article>
              ))}
            </Container>
            <Container tag="div" className="reservation-divider" />
            <h3>{t("Term & Conditions")}</h3>
            <ul className="reservation-terms">
              <li>{t("Vehicle availability is subject to owner confirmation.")}</li>
              <li>{t("Refundable if the vehicle becomes unavailable.")}</li>
              <li>
                DCAR acts as a consignment platform and does not own the vehicle
                unless stated otherwise.
              </li>
              <li>
                DCAR reserves the right to forfeit the reservation fee if the
                transaction fails due to buyer non-cooperation, inaccurate
                information, or missed appointments.
              </li>
            </ul>
            <Container tag="div" className="reservation-agreement">
              <article className="checkbox-wrapper agreement-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(event) => setAgreed(event.target.checked)}
                  />
                  <span className="checkbox" />
                </label>
              </article>
              <p className="reservation-agreement-copy">
                {t("I have read and agree to the Reservation")}{" "}
                <button type="button" onClick={() => setShowPolicy(true)}>
                  <u>{t("Terms & Conditions")}</u>
                </button>
              </p>
            </Container>
            <button
              type="button"
              className="action-modal-primary"
              disabled={
                !agreed ||
                !productId ||
                createOrderLoading ||
                validatingReferral
              }
              onClick={handleReserve}
            >
              {createOrderLoading ? "Reserving..." : "Reservation Request"}
              {!createOrderLoading && <Icon icon="charm:shield-tick" />}
            </button>
          </MDBModalBody>
        }
      />
      <PolicyReaderModal
        show={showPolicy}
        policy={reservationPolicy}
        onClose={() => setShowPolicy(false)}
      />
    </>
  );
}
